import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Sparkles, FileText } from "lucide-react";
import EquityCurve from "@/components/EquityCurve";
import { fmtUSD, gainClass } from "@/lib/format";

export default function Report() {
  const [metrics, setMetrics] = useState(null);
  const [coach, setCoach] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/digest/mine/metrics").then(({ data }) => setMetrics(data)).catch(() => {});
  }, []);

  const getCoach = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/digest/mine/generate");
      if (data.metrics) setMetrics(data.metrics);
      setCoach(data.coach || null);
      if (!data.coach?.summary) toast.info("Coach summary unavailable right now");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not generate the coach's take");
    } finally {
      setBusy(false);
    }
  };

  const fut = metrics?.futures;
  const hasFutures = fut && (fut.trades_count > 0 || fut.current_margin_used > 0);
  const period = metrics?.period
    ? `${new Date(metrics.period.start).toLocaleDateString()} – ${new Date(metrics.period.end).toLocaleDateString()}`
    : "";

  return (
    <div className="space-y-8" data-testid="report-root">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-3xl font-semibold flex items-center gap-2">
            <FileText size={22} /> Weekly report
          </h1>
          <p className="text-sm text-gray-500 mt-1" data-testid="report-period">{period}</p>
        </div>
        <Button
          onClick={getCoach}
          disabled={busy}
          data-testid="report-coach-btn"
          className="rounded-full bg-brand text-white hover:bg-brand-deep"
        >
          <Sparkles size={13} className="mr-2" /> {busy ? "Thinking…" : "Get coach's take"}
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="report-stats">
        <Tile label="Realized PnL" value={metrics ? fmtUSD(metrics.realized_pnl) : "—"} cls={metrics ? gainClass(metrics.realized_pnl) : ""} testId="report-realized" />
        <Tile label="Win rate" value={metrics ? `${metrics.win_rate}%` : "—"} testId="report-winrate" />
        <Tile label="Trades" value={metrics ? `${metrics.trades_count} (${metrics.wins}W/${metrics.losses}L)` : "—"} testId="report-trades" />
        <Tile
          label="Best / worst"
          value={
            metrics
              ? `${metrics.best_trade ? `${metrics.best_trade.symbol} +${fmtUSD(metrics.best_trade.pnl)}` : "—"} / ${
                  metrics.worst_trade ? `${metrics.worst_trade.symbol} ${fmtUSD(metrics.worst_trade.pnl)}` : "—"
                }`
              : "—"
          }
          testId="report-bestworst"
        />
      </div>

      {hasFutures && (
        <Card className="border border-gray-200 rounded-2xl p-6" data-testid="report-futures-block">
          <p className="text-[10px] uppercase font-bold tracking-[0.1em] text-gray-500">Futures</p>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-3">
            <Tile label="Fills" value={String(fut.trades_count)} plain />
            <Tile label="Contracts" value={String(fut.contracts_traded)} plain />
            <Tile label="Win rate" value={`${fut.win_rate}% (${fut.wins}W/${fut.losses}L)`} plain />
            <Tile label="Realized" value={fmtUSD(fut.realized_pnl)} cls={gainClass(fut.realized_pnl)} plain />
            <Tile
              label="Margin in use"
              value={`${fmtUSD(fut.current_margin_used)} (${fut.margin_utilization_pct}%)`}
              cls={fut.margin_utilization_pct > 40 ? "text-amber-600" : ""}
              plain
            />
          </div>
        </Card>
      )}

      <Card className="border border-gray-200 rounded-2xl p-6" data-testid="report-equity-card">
        <p className="text-[10px] uppercase font-bold tracking-[0.1em] text-gray-500 mb-4">
          Equity curve · 30 days
        </p>
        <EquityCurve days={30} height={260} />
      </Card>

      {coach && (
        <Card className="border border-gray-200 rounded-2xl p-6" data-testid="report-coach-card">
          <p className="font-display font-semibold text-lg flex items-center gap-2">
            <Sparkles size={15} /> Coach's take
          </p>
          {coach.summary && (
            <p className="text-sm text-gray-800 leading-relaxed mt-3" data-testid="report-coach-summary">
              {coach.summary}
            </p>
          )}
          {coach.recommendations?.length > 0 && (
            <ul className="mt-3 space-y-1.5">
              {coach.recommendations.map((r, i) => (
                <li key={i} className="text-sm text-gray-700">• {r}</li>
              ))}
            </ul>
          )}
        </Card>
      )}
    </div>
  );
}

function Tile({ label, value, cls = "", testId, plain }) {
  const inner = (
    <>
      <p className="text-[10px] uppercase font-bold tracking-[0.1em] text-gray-500">{label}</p>
      <p className={`font-display text-lg font-medium tabular-nums mt-1 ${cls}`} data-testid={testId}>
        {value}
      </p>
    </>
  );
  if (plain) return <div>{inner}</div>;
  return <Card className="border border-gray-200 rounded-2xl p-5">{inner}</Card>;
}
